import React from 'react'
import type { Block } from '../../types'
import { PropertyLabel, PropertyInput } from './shared'

interface DividerPropertiesProps {
  block: Block
  onUpdateBlock: (id: string, updates: Partial<Block>) => void
}

const LINE_STYLES = [
  { value: 'none', label: 'Spacer' },
  { value: 'solid', label: 'Solid' },
  { value: 'dashed', label: 'Dashed' },
  { value: 'dotted', label: 'Dotted' },
  { value: 'gradient', label: 'Gradient' },
  { value: 'wave', label: 'Wave' },
  { value: 'zigzag', label: 'Zigzag' },
  { value: 'icon', label: 'Icon' },
]

const DIVIDER_ICONS = [
  { value: 'star', label: 'Star' },
  { value: 'sparkles', label: 'Sparkles' },
  { value: 'heart', label: 'Heart' },
  { value: 'diamond', label: 'Diamond' },
  { value: 'circle', label: 'Circle' },
  { value: 'zap', label: 'Zap' },
  { value: 'dots', label: 'Dots' },
  { value: 'dash', label: 'Dash' },
]

export default function DividerProperties({ block, onUpdateBlock }: DividerPropertiesProps) {
  const config = (block.config || {}) as Record<string, unknown>
  const style = (config.style as string) || 'solid'
  const height = (config.height as number) || 32
  const color = (config.color as string) || '#e2e8f0'

  const update = (key: string, value: unknown) => {
    onUpdateBlock(block.id, { config: { ...config, [key]: value } })
  }

  return (
    <>
      <div className="space-y-2">
        <PropertyLabel>Line Style</PropertyLabel>
        <div className="grid grid-cols-2 gap-1.5">
          {LINE_STYLES.map((opt) => (
            <button
              key={opt.value}
              onClick={() => update('style', opt.value)}
              className={`py-2 px-2 rounded-lg border text-xs font-medium transition-all ${
                style === opt.value
                  ? 'border-[#5048e5] bg-[#5048e5]/5 text-[#5048e5]'
                  : 'border-slate-200 text-slate-500 hover:border-slate-300'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <PropertyLabel>Height (px)</PropertyLabel>
        <PropertyInput
          type="number"
          value={height}
          min={8}
          max={120}
          onChange={(e) => update('height', Number(e.target.value))}
        />
      </div>

      {style === 'icon' && (
        <div className="space-y-2">
          <PropertyLabel>Icon</PropertyLabel>
          <div className="grid grid-cols-4 gap-1.5">
            {DIVIDER_ICONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => update('icon', opt.value)}
                className={`py-2 px-2 rounded-lg border text-xs font-medium transition-all ${
                  (config.icon as string || 'star') === opt.value
                    ? 'border-[#5048e5] bg-[#5048e5]/5 text-[#5048e5]'
                    : 'border-slate-200 text-slate-500 hover:border-slate-300'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {style !== 'none' && (
        <div className="space-y-2">
          <PropertyLabel>Line Color</PropertyLabel>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={color}
              onChange={(e) => update('color', e.target.value)}
              className="size-8 rounded cursor-pointer border border-slate-200"
            />
            <span className="text-xs font-mono text-slate-400">{color}</span>
          </div>
        </div>
      )}
    </>
  )
}
